import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEnvVariablesStore } from "@/store/zustand/envVariablesStore";
import { getUnexpiredToken, useCanvasId, useProjectId } from "@/utils/utils";

// Migration guide on the new docs site (Mintlify, shipped from tempo-monorepo).
const MIGRATION_GUIDE_URL = "https://docs.tempo.new/guides/migrate";

type View = "menu" | "env";

/**
 * Non-dismissable modal shown over the canvas editor now that the legacy cloud
 * app is retired. Gives users the only three things they can still do: read
 * their env vars, download their codebase, or leave.
 */
const CanvasArchivedModal = () => {
  const canvasId = useCanvasId();
  const projectId = useProjectId();
  const { session } = useSession();
  const navigate = useNavigate();

  const [view, setView] = useState<View>("menu");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!canvasId || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const token = await getUnexpiredToken(session);
      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/download-archive`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${canvasId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error("Failed to download codebase archive", e);
      setDownloadError(
        e?.response?.status === 404
          ? "We couldn't find an archived codebase for this project."
          : "Something went wrong preparing your download. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  };

  // Radix calls these on ESC / outside-click / programmatic close. Preventing
  // default keeps the modal open no matter what the user does.
  const preventClose = (e: Event) => e.preventDefault();

  return (
    <Dialog open modal>
      <DialogContent
        hideClose
        onEscapeKeyDown={preventClose}
        onPointerDownOutside={preventClose}
        onInteractOutside={preventClose}
        className="sm:max-w-[480px]"
      >
        {view === "menu" ? (
          <>
            <DialogHeader>
              <DialogTitle>Tempo's classic app has been retired</DialogTitle>
              <DialogDescription className="text-xs pt-2 leading-relaxed">
                Tempo's web app has moved to desktop. This canvas is no longer
                editable here — read the migration guide, and grab your
                environment variables and codebase below.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2 pt-3">
              <Button variant="secondary" onClick={() => setView("env")}>
                View environment variables
              </Button>
              <Button
                variant="primary"
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading
                  ? "Preparing download…"
                  : "Download codebase (.zip)"}
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  window.open(
                    MIGRATION_GUIDE_URL,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Read the migration guide ↗
              </Button>
              <Button variant="outline" onClick={() => navigate("/")}>
                Go back to the main page
              </Button>
            </div>

            {downloadError ? (
              <p className="text-xs text-destructive pt-1">{downloadError}</p>
            ) : null}
          </>
        ) : (
          <EnvVariablesView
            projectId={projectId}
            session={session}
            onBack={() => setView("menu")}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const EnvVariablesView = ({
  projectId,
  session,
  onBack,
}: {
  projectId: string | null;
  session: any;
  onBack: () => void;
}) => {
  const { envVariables, isLoading, error, fetchEnvVariables } =
    useEnvVariablesStore();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchEnvVariables(projectId, session);
    }
  }, [projectId, session, fetchEnvVariables]);

  const entries = Object.entries(envVariables || {});

  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(label);
      setTimeout(() => setCopiedKey((k) => (k === label ? null : k)), 1500);
    } catch {
      // Clipboard unavailable — no-op.
    }
  };

  const copyAll = () =>
    copy(
      "__all__",
      entries.map(([key, value]) => `${key}=${value}`).join("\n"),
    );

  return (
    <>
      <DialogHeader>
        <DialogTitle>Environment variables</DialogTitle>
        <DialogDescription className="text-xs pt-2">
          Read-only — copy what you need into your new project.
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-[320px] overflow-y-auto rounded-md border border-input divide-y divide-input">
        {isLoading ? (
          <p className="text-xs text-muted-foreground p-3">Loading…</p>
        ) : error ? (
          <p className="text-xs text-destructive p-3">
            Couldn't load environment variables.
          </p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-muted-foreground p-3">
            No environment variables are set for this project.
          </p>
        ) : (
          entries.map(([key, value]) => (
            <div
              key={key}
              className="flex items-center gap-2 p-2 text-xs font-mono"
            >
              <span className="shrink-0 max-w-[40%] truncate" title={key}>
                {key}
              </span>
              <span
                className="grow truncate text-muted-foreground"
                title={value}
              >
                {value}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 shrink-0"
                onClick={() => copy(key, value)}
              >
                {copiedKey === key ? "Copied" : "Copy"}
              </Button>
            </div>
          ))
        )}
      </div>

      <div className="flex justify-between gap-2 pt-1">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        {entries.length > 0 ? (
          <Button variant="secondary" onClick={copyAll}>
            {copiedKey === "__all__" ? "Copied all" : "Copy all (.env)"}
          </Button>
        ) : null}
      </div>
    </>
  );
};

export default CanvasArchivedModal;
